// Widok dnia /v2 — składa dzień z danych API i liczy podsumowania prawej kolumny.
// Różnice względem useDayTimeline (klasyczny raport dnia):
//  • posiłki zaplanowane też trafiają na oś (przygaszone, z etykietą „plan"),
//  • sen to jedno zdarzenie z zakresem zaśnięcie → pobudka, przy godzinie pobudki,
//  • zdarzenia dostają gotowe etykiety do renderu (tytuł, meta, opis),
//    żeby komponent osi nie musiał znać kształtu danych z API.

import { intlLocale } from '@/composables/useLanguage.js';
import { t } from '@/i18n/index.js';
import { plannerTitle } from '@/composables/useLudzie.js';
import { carePlannerTitle as careTitle } from '@/composables/useObslugi.js';

const pad = (n) => String(n).padStart(2, '0');

// Paleta zdarzeń. `color` to wersja mocna (kropki filtrów, paski podsumowań),
// `soft` — jasna, na wstążkę osi i bloki tygodnia. `plan` dotyczy tego, co się
// jeszcze nie stało: ten sam odcień, tylko bledszy.
export const V2_KINDS = {
    sleep:    { get label() { return t('planner.kinds.sleep'); }, color: '#6E7BA6', soft: '#B7BED7' },
    bedtime:  { get label() { return t('planner.kinds.sleep'); }, color: '#6E7BA6', soft: '#B7BED7' },
    nap:      { get label() { return t('planner.kinds.nap'); }, color: '#8A93BC', soft: '#C6CBE0' },
    workout:  { get label() { return t('planner.kinds.workout'); }, color: '#C06B6B', soft: '#E3B3B3', plan: '#F0D6D6' },
    meal:     { get label() { return t('planner.kinds.meal'); }, color: '#C08457', soft: '#E5C4A5' },
    'meal-planned': { get label() { return t('planner.kinds.meal'); }, color: '#C08457', soft: '#E5C4A5', plan: '#EFDCC8' },
    task:      { get label() { return t('planner.kinds.task'); }, color: '#5B8C6E', soft: '#B6CFC1' },
    // Termin projektu — wpis całodniowy w dniu due_on.
    'project-due': { get label() { return t('planner.kinds.project'); }, color: '#5B8C6E', soft: '#B6CFC1' },
    // Ważna data z Ludzi (urodziny, imieniny, rocznica) — wpis całodniowy w jej dniu.
    'person-date': { get label() { return t('planner.kinds.personDate'); }, color: '#B5647A', soft: '#E7C3CC' },
    // Obsługa zwierzęcia albo sprzętu (szczepienie, przegląd) — wpis całodniowy w dniu następnego terminu.
    care: { get label() { return t('planner.kinds.care'); }, color: '#8C6E3F', soft: '#DCCBB0' },
    note:     { get label() { return t('planner.kinds.note'); }, color: '#8E7BA6', soft: '#CFC3DA' },
    report:   { get label() { return t('planner.kinds.report'); }, color: '#7D9AA6', soft: '#C8DADF' },
    event:    { get label() { return t('planner.kinds.event'); }, color: '#3F8A8C', soft: '#B3D4D4' },
    activity: { get label() { return t('planner.kinds.activity'); }, color: '#B08A3E', soft: '#E2CFA6' },
};

// Tło wstążki i pasków — jasny piaskowy, wspólny dla całego planera.
export const V2_TRACK = '#EFE9DF';

// Rodzaj, którego nie znamy (nowy typ z API, wpis bez kategorii). Neutralny
// piaskowy z palety planera — jeden zestaw zamiast fallbacków per komponent.
export const V2_KIND_FALLBACK = { color: '#A29A8E', soft: '#D8D2C6', plan: V2_TRACK };

// Sekcja „Pokaż" w sidebarze — jeden przełącznik może obejmować kilka rodzajów.
export const V2_FILTERS = [
    { key: 'events',   get label() { return t('planner.filters.events'); }, color: '#3F8A8C',              kinds: ['event', 'person-date', 'care'] },
    { key: 'meals',   get label() { return t('planner.filters.meals'); },    color: V2_KINDS.meal.color,    kinds: ['meal', 'meal-planned'] },
    { key: 'sleep',    get label() { return t('planner.filters.sleep'); },        color: V2_KINDS.sleep.color,   kinds: ['sleep', 'bedtime', 'nap'] },
    { key: 'tasks',    get label() { return t('planner.filters.tasks'); },    color: V2_KINDS.task.color,    kinds: ['task', 'project-due'] },
    { key: 'workouts', get label() { return t('planner.filters.workouts'); },   color: V2_KINDS.workout.color, kinds: ['workout'] },
    { key: 'reports',  get label() { return t('planner.filters.reports'); }, color: V2_KINDS.report.color,  kinds: ['report'] },
    { key: 'activities', get label() { return t('planner.filters.activities'); }, color: V2_KINDS.activity.color, kinds: ['activity'] },
];

// Trening bez statusu (starsze wpisy) traktujemy jako wykonany — status to
// nowe pole, a wszystko, co było zapisane wcześniej, już się wydarzyło.
export const isWorkoutDone = (workout) => (workout?.status ?? 'done') !== 'planned';

// Zadanie po terminie: nieodhaczone, a jego godzina (albo cały dzień) już minęły.
export const isTaskOverdue = (task, now = new Date()) => {
    if (!task || task.done) return false;
    if (task.due_at) return new Date(task.due_at).getTime() < now.getTime();
    if (!task.planned_for) return false;
    const day = new Date(task.planned_for);
    day.setHours(23, 59, 59, 999);
    return day.getTime() < now.getTime();
};

// Termin zadania: godzina, jeśli jest, inaczej koniec zaplanowanego dnia.
export const taskDeadline = (task) => {
    if (!task) return null;
    if (task.due_at) return new Date(task.due_at);
    if (!task.planned_for) return null;
    const day = new Date(task.planned_for);
    day.setHours(23, 59, 59, 999);
    return day;
};

// „25 min", „14 h", „3 dni" — o tyle zadanie się spóźnia. Minuty zaokrąglamy
// w górę, żeby świeżo minięty termin nie pokazywał „0 min".
export const overdueFor = (task, now = new Date()) => {
    const deadline = taskDeadline(task);
    if (!deadline) return null;
    const minutes = Math.floor((now.getTime() - deadline.getTime()) / 60000);
    if (minutes < 60) return `${Math.max(1, minutes)} min`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} h`;
    const days = Math.floor(hours / 24);
    return t('planner.overdue.days', { n: days }, days);
};

// Podpis pod paskiem zadań: „oba po terminie" brzmi naturalniej niż „2 z 2".
export const overdueNote = (overdue, total) => {
    if (!overdue) return null;
    if (overdue === 1) return t('planner.overdue.one');
    if (overdue === 2 && total === 2) return t('planner.overdue.both');
    if (overdue === total) return t('planner.overdue.all');
    return t('planner.overdue.count', { n: overdue });
};

export function useV2Day() {
    // Lokalny klucz dnia — bez konwersji na UTC, by nie przesuwać doby.
    const dayKey = (value) => {
        if (!value) return null;
        const d = value instanceof Date ? value : new Date(value);
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    };

    const shiftDays = (date, delta) => {
        const d = new Date(date);
        d.setDate(d.getDate() + delta);
        d.setHours(0, 0, 0, 0);
        return d;
    };

    const hm = (value) => {
        if (!value) return null;
        const d = value instanceof Date ? value : new Date(value);
        return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };

    // Czas trwania w stylu widoku: „7h 35" / „32 min".
    const dur = (ms) => {
        const totalMin = Math.round((ms ?? 0) / 60000);
        const h = Math.floor(totalMin / 60);
        const m = totalMin % 60;
        if (h && m) return `${h}h ${pad(m)}`;
        if (h) return `${h}h`;
        return `${m} min`;
    };

    const durMin = (seconds) => {
        const totalMin = Math.round((seconds ?? 0) / 60);
        const h = Math.floor(totalMin / 60);
        const m = totalMin % 60;
        if (h && m) return `${h}h ${pad(m)} min`;
        if (h) return `${h}h`;
        return `${m} min`;
    };

    const spanMs = (from, to) => {
        if (!from || !to) return 0;
        return Math.max(0, new Date(to).getTime() - new Date(from).getTime());
    };

    // Dystans: metry → „5,2 km" / „640 m".
    const distance = (meters) => {
        if (meters == null) return null;
        if (meters < 1000) return `${meters} m`;
        return `${(meters / 1000).toLocaleString(intlLocale(), { minimumFractionDigits: 1, maximumFractionDigits: 1 })} km`;
    };

    // Tempo min/km z dystansu i czasu — treningi nie mają w API tętna ani tempa.
    const pace = (meters, seconds) => {
        if (!meters || !seconds || meters < 100) return null;
        const secPerKm = seconds / (meters / 1000);
        return `${Math.floor(secPerKm / 60)}:${pad(Math.round(secPerKm % 60))}`;
    };

    const qualityWord = (q) => {
        if (q == null) return null;
        if (q < 40) return t('planner.quality.poor');
        if (q < 70) return t('planner.quality.average');
        if (q < 85) return t('planner.quality.good');
        return t('planner.quality.great');
    };

    // Skrót składników do drugiego wiersza wpisu: „owsianka, borówki, masło orzechowe".
    const ingredientLine = (ingredients, max = 4) => {
        const names = (ingredients ?? [])
            .map(i => i.product_name ?? i.product?.name)
            .filter(Boolean);
        if (!names.length) return null;
        if (names.length <= max) return names.join(', ');
        return `${names.slice(0, max).join(', ')} + ${names.length - max}`;
    };

    // Składniki wpisu; dla starych wpisów bez własnych — dopasowanie po nazwie posiłku.
    const entryIngredients = (entry, mealsCatalog) => {
        if (entry.ingredients?.length) return entry.ingredients;
        const norm = (entry.name ?? '').trim().toLowerCase();
        if (!norm) return null;
        const meal = (mealsCatalog ?? []).find(m => (m.name ?? '').trim().toLowerCase() === norm);
        return meal?.ingredients?.length ? meal.ingredients : null;
    };

    // Składnik wpisu to kopia (nazwa + gramy) bez powiązania z produktem. Produkt
    // z katalogu dopasowujemy po nazwie, żeby oś pokazała porcje („2 × sztuka").
    const withProducts = (ingredients, byName) => (ingredients && byName.size
        ? ingredients.map(i => (i.product ? i : { ...i, product: byName.get((i.product_name ?? '').trim().toLowerCase()) ?? null }))
        : ingredients);

    // Zadania dnia rozdzielone na to, co ma godzinę (oś), i to, co jej nie ma.
    // Zadania projektów są zwykłymi zadaniami; sam projekt z terminem (due_on) na ten
    // dzień daje wpis całodniowy „Termin: …" — zakończone projekty pomijamy.
    // Ważne daty z Ludzi (person_dates z /planner) też są wpisami całodniowymi: „🎂 Urodziny: Kasia (30)".
    // Obsługi zwierząt i sprzętów (cares z /planner) — wpis całodniowy „💉 Burek: szczepienie".
    const splitTasks = ({ day, tasks = [], checklists = [], personDates = [], cares = [] }) => {
        const key = dayKey(day);
        const dayTasks = tasks.filter(t => dayKey(t.planned_for) === key);

        const careDays = cares
            .filter(c => c.due_on === key)
            .map(c => ({
                id: `care-${c.uuid}-${c.due_on}`,
                kind: 'care',
                at: shiftDays(day, 0),
                allDay: true,
                title: careTitle(c),
                timeLabel: t('planner.event.allDay'),
                meta: [],
                detail: null,
                care: c,
            }));

        const personDays = personDates
            .filter(pd => pd.date === key)
            .map(pd => ({
                id: `person-date-${pd.uuid}-${pd.date}`,
                kind: 'person-date',
                at: shiftDays(day, 0),
                allDay: true,
                title: plannerTitle(pd),
                timeLabel: t('planner.event.allDay'),
                meta: [],
                detail: null,
                personDate: pd,
            }));

        const dues = checklists
            .filter(cl => !cl.finished_at && cl.due_on && String(cl.due_on).slice(0, 10) === key)
            .map(cl => ({
                id: 'project-due-' + cl.uuid,
                kind: 'project-due',
                at: shiftDays(day, 0),
                allDay: true,
                title: t('planner.event.projectDue',{ title: cl.title || t('planner.kinds.project') }),
                timeLabel: t('planner.event.allDay'),
                meta: [],
                detail: null,
                project: cl,
            }));

        const scheduled = dayTasks.filter(t => t.due_at).map(t => ({
            id: 'task-' + t.uuid,
            kind: 'task',
            at: new Date(t.due_at),
            title: t.text,
            timeLabel: hm(t.due_at),
            meta: [],
            detail: t.checklist_title || null,
            task: t,
        }));

        const unscheduled = dayTasks
            .filter(t => !t.due_at)
            .sort((a, b) => (a.done ? 1 : 0) - (b.done ? 1 : 0) || (a.position ?? 0) - (b.position ?? 0));

        // Zaległe zadania dnia — od najstarszego terminu. Widok pokazuje je wprost
        // (pasek nad osią i sekcja w podsumowaniu), bo inaczej giną między wpisami.
        const overdue = dayTasks
            .filter(t => isTaskOverdue(t))
            .sort((a, b) => (taskDeadline(a)?.getTime() ?? 0) - (taskDeadline(b)?.getTime() ?? 0));

        return {
            events: [...personDays, ...careDays, ...dues, ...scheduled],
            unscheduled,
            overdue,
            progress: {
                done: dayTasks.filter(t => t.done).length,
                total: dayTasks.length,
                overdue: overdue.length,
            },
        };
    };

    // Buduje posortowaną listę zdarzeń dnia z gotowymi etykietami.
    const buildEvents = ({ day, mealEntries = [], workouts = [], activities = [], sleepEntries = [], dayReports = [], mealsCatalog = [], taskEvents = [], products = [], calendarEvents = [] }) => {
        const key = dayKey(day);
        const productsByName = new Map(products.map(p => [(p.name ?? '').trim().toLowerCase(), p]));
        const nextKey = dayKey(shiftDays(day, 1));
        const events = [...taskEvents];

        // Wydarzenia z kalendarza: całodniowe otwierają dzień, a te z godziną trafiają
        // na oś także w dniach, przez które tylko przechodzą (np. wyjazd do jutra).
        const dayStart = shiftDays(day, 0);
        const dayEnd = shiftDays(day, 1);
        for (const c of calendarEvents) {
            const base = {
                id: 'event-' + c.uuid,
                kind: 'event',
                title: c.title,
                meta: [],
                detail: c.description || null,
                location: c.location || null,
                linked: !!c.linked,
                calendarEvent: c,
            };

            if (c.all_day) {
                if (!c.start_date || c.start_date > key || c.end_date < key) continue;
                const days = Math.round((new Date(c.end_date) - new Date(c.start_date)) / 86400000) + 1;
                events.push({
                    ...base,
                    at: dayStart,
                    allDay: true,
                    timeLabel: t('planner.event.allDay'),
                    meta: days > 1 ? [t('planner.event.dayOf', { n: Math.round((new Date(key) - new Date(c.start_date)) / 86400000) + 1, total: days })] : [],
                });
                continue;
            }

            const start = new Date(c.starts_at);
            const end = new Date(c.ends_at);
            const touches = start < dayEnd && (end > dayStart || (end.getTime() === start.getTime() && start >= dayStart));
            if (!touches) continue;
            // Poza dzisiejszą dobą zamiast godziny podajemy dzień, w którym wydarzenie się zaczyna / kończy.
            const edge = (d) => (dayKey(d) === key ? hm(d) : d.toLocaleDateString(intlLocale(), { day: 'numeric', month: 'short' }) + ' ' + hm(d));
            events.push({
                ...base,
                at: start < dayStart ? dayStart : start,
                timeLabel: end > start ? `${edge(start)} – ${edge(end)}` : hm(start),
                meta: end > start ? [dur(end - start)] : [],
            });
        }

        // Posiłki — zjedzone i zaplanowane; plan jest przygaszony i oznaczony.
        for (const e of mealEntries) {
            if (dayKey(e.planned_at) !== key) continue;
            const planned = e.status !== 'eaten';
            const ingredients = withProducts(entryIngredients(e, mealsCatalog), productsByName);
            events.push({
                id: 'meal-' + (e.uuid ?? e.id),
                kind: planned ? 'meal-planned' : 'meal',
                at: new Date(e.planned_at),
                title: e.name ?? t('planner.kinds.meal'),
                timeLabel: hm(e.planned_at),
                meta: [`${planned ? '~' : ''}${e.calories ?? 0} kcal`],
                detail: ingredientLine(ingredients),
                // Zaplanowany posiłek nie jest „mniej ważny" — ma tylko inny stan,
                // dlatego zamiast wygaszenia dostaje własny znacznik i wstążkę.
                planned,
                entry: e,
                ingredients,
            });
        }

        // Treningi — tytuł nosi dystans, bo to on odróżnia bieg od biegu.
        // Zaplanowany trening nie ma jeszcze czasu ani kalorii.
        for (const w of workouts) {
            if (dayKey(w.performed_at) !== key) continue;
            const dist = distance(w.distance_meters);
            const p = pace(w.distance_meters, w.duration_seconds);
            const done = isWorkoutDone(w);
            events.push({
                id: 'workout-' + (w.uuid ?? w.id),
                kind: 'workout',
                at: new Date(w.performed_at),
                title: [w.type?.name ?? t('planner.kinds.workout'), dist].filter(Boolean).join(' '),
                timeLabel: hm(w.performed_at),
                meta: w.duration_seconds ? [durMin(w.duration_seconds)] : [],
                detail: [
                    p ? t('planner.workout.pace', { pace: p }) : null,
                    w.active_calories != null ? `${w.active_calories} kcal` : null,
                ].filter(Boolean).join(' · ') || null,
                planned: !done,
                workout: w,
            });
        }

        // Czynności z rozmowy z asystentem (prysznic, spacer) — przy godzinie rozpoczęcia;
        // ta, która jeszcze trwa, nie ma czasu trwania.
        for (const a of activities) {
            if (dayKey(a.started_at) !== key) continue;
            events.push({
                id: 'activity-' + a.uuid,
                kind: 'activity',
                at: new Date(a.started_at),
                title: a.name,
                timeLabel: a.ended_at ? `${hm(a.started_at)}–${hm(a.ended_at)}` : hm(a.started_at),
                meta: a.duration_seconds != null ? [durMin(a.duration_seconds)] : [],
                detail: null,
                activity: a,
            });
        }

        // Sen: wpis z pobudką dziś → jedno zdarzenie z zakresem, przy godzinie pobudki.
        // Wieczorne zaśnięcie (wpis dotyczący jutrzejszej pobudki) zamyka dzień.
        // Godziny są w API opcjonalne, więc wpis bywa niedokończony — wtedy mówimy
        // o tym wprost, zamiast pomijać go na osi.
        for (const s of sleepEntries) {
            const napCount = (s.naps ?? []).length;
            const sleepDetail = () => [
                s.quality != null ? t('planner.sleep.qualityWord', { quality: s.quality, word: qualityWord(s.quality) }) : null,
                napCount ? t('planner.sleep.naps', { n: napCount }, napCount) : null,
                s.notes || null,
            ].filter(Boolean).join(' · ') || null;

            if (s.wake_up_at && dayKey(s.wake_up_at) === key) {
                const ms = spanMs(s.bedtime, s.wake_up_at);
                events.push({
                    id: 'sleep-' + (s.uuid ?? s.id),
                    kind: 'sleep',
                    at: new Date(s.wake_up_at),
                    title: t('planner.kinds.sleep'),
                    timeLabel: s.bedtime ? `${hm(s.bedtime)} – ${hm(s.wake_up_at)}` : hm(s.wake_up_at),
                    meta: ms ? [dur(ms)] : [],
                    detail: sleepDetail(),
                    // Brak zaśnięcia = nie wiadomo, ile ten sen trwał.
                    pending: !s.bedtime ? t('planner.sleep.noBedtime') : null,
                    sleep: s,
                });
            } else if (!s.wake_up_at && dayKey(s.date) === key) {
                // Wpis założony wieczorem, pobudka jeszcze niedopisana.
                const at = s.bedtime && dayKey(s.bedtime) === key
                    ? new Date(s.bedtime)
                    : new Date(new Date(day).setHours(0, 0, 0, 0));
                events.push({
                    id: 'sleep-open-' + (s.uuid ?? s.id),
                    kind: 'sleep',
                    at,
                    title: t('planner.kinds.sleep'),
                    timeLabel: s.bedtime ? `${hm(s.bedtime)} – ?` : null,
                    meta: [],
                    detail: sleepDetail(),
                    pending: t('planner.sleep.noWakeUp'),
                    sleep: s,
                });
            }

            if (s.bedtime && dayKey(s.date) === nextKey) {
                events.push({
                    id: 'bedtime-' + (s.uuid ?? s.id),
                    kind: 'bedtime',
                    at: new Date(s.bedtime),
                    title: t('planner.sleep.goingToBed'),
                    timeLabel: hm(s.bedtime),
                    meta: [],
                    detail: null,
                    // Dopóki nie ma pobudki, ten wpis czeka na dopisanie rano.
                    pending: s.wake_up_at ? null : t('planner.sleep.noWakeUp'),
                    sleep: s,
                });
            }
            for (const nap of s.naps ?? []) {
                if (!nap.start_at || dayKey(nap.start_at) !== key) continue;
                const ms = spanMs(nap.start_at, nap.end_at);
                events.push({
                    id: 'nap-' + (nap.uuid ?? nap.id),
                    kind: 'nap',
                    at: new Date(nap.start_at),
                    title: t('planner.kinds.nap'),
                    timeLabel: nap.end_at ? `${hm(nap.start_at)} – ${hm(nap.end_at)}` : hm(nap.start_at),
                    meta: ms ? [dur(ms)] : [],
                    detail: [
                        nap.quality != null ? t('planner.sleep.quality', { quality: nap.quality }) : null,
                        nap.notes || null,
                    ].filter(Boolean).join(' · ') || null,
                    nap,
                });
            }
        }

        // Raporty dnia — treść mówi sama za siebie, więc idzie jako cytat.
        for (const r of dayReports) {
            if (dayKey(r.reported_at) !== key) continue;
            events.push({
                id: 'report-' + (r.uuid ?? r.id),
                kind: 'report',
                at: new Date(r.reported_at),
                quote: r.content,
                timeLabel: hm(r.reported_at),
                meta: [],
                detail: t('planner.report.detail', { time: hm(r.reported_at) }),
                report: r,
            });
        }

        events.sort((a, b) => a.at - b.at);

        // Przy wstążce pokazujemy dokładny moment (godzina i minuty).
        for (const ev of events) ev.atLabel = hm(ev.at);
        return events;
    };

    // Co i czym edytować dla danego zdarzenia — jedno miejsce mapowania
    // rodzaju zdarzenia na typ wpisu w modalu. null = brak edycji.
    const editTarget = (ev) => {
        switch (ev.kind) {
            case 'meal':
            case 'meal-planned': return { type: 'meal', entry: ev.entry };
            case 'workout': return { type: 'workout', entry: ev.workout };
            // Drzemki i zaśnięcie są częścią wpisu snu, więc edytujemy ten wpis.
            case 'sleep':
            case 'bedtime':
            case 'nap': return { type: 'sleep', entry: ev.sleep };
            case 'task': return { type: 'task', entry: ev.task };
            case 'report': return { type: 'report', entry: ev.report };
            case 'event': return { type: 'event', entry: ev.calendarEvent };
            default: return null;
        }
    };

    // Podsumowanie dnia. Cele mamy w profilu tylko dla kalorii i makr —
    // sen i ruch odnosimy do maksimum z ostatnich 7 dni, żeby nie zmyślać celów.
    const daySummary = ({ events, profile, sleepBest = 0, moveBest = 0, sleepAvgMs = 0, taskProgress = null }) => {
        const eaten = events.filter(e => e.kind === 'meal');
        // Ruch to tylko treningi wykonane — zaplanowane jeszcze nic nie zmieniły.
        const workouts = events.filter(e => e.kind === 'workout' && !e.planned);
        const sleepEvent = events.find(e => e.kind === 'sleep');

        const sum = (key) => eaten.reduce((a, e) => a + (e.entry[key] ?? 0), 0);
        const sleepMs = sleepEvent ? spanMs(sleepEvent.sleep.bedtime, sleepEvent.sleep.wake_up_at) : 0;
        const moveSeconds = workouts.reduce((a, e) => a + (e.workout.duration_seconds ?? 0), 0);
        const moveKcal = workouts.reduce((a, e) => a + (e.workout.active_calories ?? 0), 0);

        const kcal = sum('calories');
        const protein = sum('protein');
        const kcalGoal = profile?.calorie_goal ?? null;
        const proteinGoal = profile?.protein_goal ?? null;

        const pct = (value, of) => (of ? Math.min(100, Math.round((value / of) * 100)) : 0);

        const metrics = [
            {
                key: 'sleep',
                label: t('planner.summary.sleep'),
                value: sleepMs ? dur(sleepMs) : '—',
                sub: sleepAvgMs ? t('planner.summary.sleepAvg', { duration: dur(sleepAvgMs) }) : null,
                width: pct(sleepMs, sleepBest || sleepMs),
                color: V2_KINDS.sleep.color,
            },
            {
                key: 'calories',
                label: t('planner.summary.calories'),
                value: String(kcal),
                goal: kcalGoal ? `${kcalGoal}` : null,
                sub: kcalGoal ? null : t('planner.summary.noGoal'),
                width: pct(kcal, kcalGoal),
                over: kcalGoal ? kcal > kcalGoal : false,
                color: V2_KINDS.meal.color,
            },
            {
                key: 'move',
                label: t('planner.summary.move'),
                value: moveSeconds ? durMin(moveSeconds) : '—',
                sub: moveKcal ? t('planner.summary.activeKcal', { kcal: moveKcal }) : null,
                width: pct(moveSeconds, moveBest || moveSeconds),
                color: V2_KINDS.workout.color,
            },
            // Gdy API nie zna zadań, ostatni wiersz pokazuje białko z celu w profilu.
            taskProgress
                ? {
                    key: 'tasks',
                    label: t('planner.summary.tasks'),
                    value: String(taskProgress.done),
                    goal: String(taskProgress.total),
                    // Zaległości mówimy wprost i przestawiamy pasek na terakotę —
                    // „0 / 2" samo nie zdradza, że oba terminy już minęły.
                    sub: taskProgress.overdue ? overdueNote(taskProgress.overdue, taskProgress.total) : null,
                    over: !!taskProgress.overdue,
                    width: pct(taskProgress.done, taskProgress.total),
                    color: V2_KINDS.task.color,
                }
                : {
                    key: 'protein',
                    label: t('planner.summary.protein'),
                    value: `${protein} g`,
                    goal: proteinGoal ? `${proteinGoal} g` : null,
                    sub: proteinGoal ? null : t('planner.summary.noGoal'),
                    width: pct(protein, proteinGoal),
                    over: proteinGoal ? protein > proteinGoal : false,
                    color: V2_KINDS.task.color,
                },
        ];

        const macros = [
            // `primary` — makro z paskiem do celu w prawej kolumnie. Cukry i błonnik
            // czyta się jako sam odczyt, żeby karta nie zamieniła się w ścianę pasków.
            ...(taskProgress ? [{ label: t('planner.summary.protein'), value: protein, goal: proteinGoal, color: V2_KINDS.task.color, primary: true }] : []),
            { label: t('planner.summary.carbs'), value: sum('carbs'), goal: profile?.carbs_goal ?? null, color: V2_KINDS.report.color, primary: true },
            { label: t('planner.summary.fat'), value: sum('fat'),   goal: profile?.fat_goal ?? null,   color: V2_KINDS.meal.color,   primary: true },
            { label: t('planner.summary.sugar'), value: sum('sugar'), goal: profile?.sugar_goal ?? null, color: V2_KINDS.note.color },
            { label: t('planner.summary.fibre'), value: sum('fiber'), goal: null,                        color: V2_KINDS.task.color },
        ].map(m => ({ ...m, width: m.goal ? Math.min(100, Math.round((m.value / m.goal) * 100)) : 0 }));

        return {
            metrics,
            macros,
            mealCount: eaten.length,
            price: sum('price'),
        };
    };

    // Ostatnie 7 dni snu, kończąc na wybranym dniu — słupki + średnia.
    const sleepWeek = ({ day, sleepEntries = [] }) => {
        const bars = [];
        for (let i = 6; i >= 0; i--) {
            const d = shiftDays(day, -i);
            const key = dayKey(d);
            const entry = sleepEntries.find(s => s.wake_up_at && dayKey(s.wake_up_at) === key);
            bars.push({
                key,
                ms: entry ? spanMs(entry.bedtime, entry.wake_up_at) : 0,
                isCurrent: i === 0,
            });
        }
        const filled = bars.filter(b => b.ms > 0);
        const best = Math.max(0, ...bars.map(b => b.ms));
        const avg = filled.length ? filled.reduce((a, b) => a + b.ms, 0) / filled.length : 0;
        return { bars, best, avg };
    };

    // Maksimum minut ruchu w ostatnich 7 dniach — skala paska „Ruch".
    const moveWeekBest = ({ day, workouts = [] }) => {
        let best = 0;
        for (let i = 6; i >= 0; i--) {
            const key = dayKey(shiftDays(day, -i));
            const seconds = workouts
                .filter(w => dayKey(w.performed_at) === key && isWorkoutDone(w))
                .reduce((a, w) => a + (w.duration_seconds ?? 0), 0);
            best = Math.max(best, seconds);
        }
        return best;
    };

    // Dni miesiąca, w których cokolwiek się dzieje — kropki w mini-kalendarzu.
    const activeDayKeys = ({ mealEntries = [], workouts = [], activities = [], sleepEntries = [], dayReports = [], calendarEvents = [] }) => {
        const keys = new Set();
        for (const c of calendarEvents) keys.add(c.all_day ? c.start_date : dayKey(c.starts_at));
        for (const e of mealEntries) if (e.planned_at) keys.add(dayKey(e.planned_at));
        for (const w of workouts) if (w.performed_at) keys.add(dayKey(w.performed_at));
        for (const a of activities) keys.add(dayKey(a.started_at));
        for (const s of sleepEntries) if (s.wake_up_at) keys.add(dayKey(s.wake_up_at));
        for (const r of dayReports) if (r.reported_at) keys.add(dayKey(r.reported_at));
        keys.delete(null);
        return keys;
    };

    // Dni z niezrobionymi zadaniami (z godziną i bez) i ich liczba — osobny znacznik
    // w mini-kalendarzu, żeby zaległe i czekające zadania nie ginęły wśród kropek.
    const openTaskDays = (items = []) => {
        const counts = new Map();
        for (const t of items) {
            if (t.done || !t.planned_for) continue;
            // planned_for to sam dzień (YYYY-MM-DD) — bez przeliczania stref.
            const key = String(t.planned_for).slice(0, 10);
            counts.set(key, (counts.get(key) ?? 0) + 1);
        }
        return counts;
    };

    return {
        openTaskDays,
        dayKey, shiftDays, hm, dur, durMin, spanMs, distance, pace,
        buildEvents, splitTasks, editTarget, daySummary, sleepWeek, moveWeekBest, activeDayKeys,
    };
}
